import React, { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import AuraLogo from '../AuraLogo';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useFeatures, ROUTE_DOMAINS } from '../../context/FeatureContext';
import {
  LayoutDashboard, Megaphone, Newspaper, MessageSquare, TrendingUp,
  BookOpen, FileText, PlaySquare, Zap, CalendarCheck, GraduationCap,
  Clock, BookMarked, Briefcase, Target, Linkedin, Brain,
  Code, Trophy,
  Users, Globe, Code2,
  Sparkles, Utensils, Car, Home, ShoppingBag, FilePen,
  Receipt, Shield, AlertCircle,
  LogOut, ChevronLeft, ChevronRight, Menu, X, Bell,
  Settings, Search, Award,
} from 'lucide-react';

/* ── Zone definitions ── */
const NAV_ZONES = [
  {
    id: 'home', label: 'Home', zicon: LayoutDashboard,
    gradient: '#1a73e8', lightBg: '#EFF6FF',
    items: [
      { to: '/',              icon: LayoutDashboard, label: 'Dashboard'     },
      { to: '/announcements', icon: Megaphone,       label: 'Announcements' },
      { to: '/campus-news',   icon: Newspaper,       label: 'Campus News'   },
      { to: '/forums',        icon: MessageSquare,   label: 'Forums'        },
      { to: '/social',        icon: TrendingUp,      label: 'Student Social' },
    ],
  },
  {
    id: 'study', label: 'Study', zicon: BookOpen,
    gradient: '#1a73e8', lightBg: '#EFF6FF',
    items: [
      { to: '/notes',      icon: FileText,     label: 'Notes'          },
      { to: '/materials',  icon: BookOpen,     label: 'Study Materials' },
      { to: '/playlists',  icon: PlaySquare,   label: 'Playlists'      },
      { to: '/flashcards', icon: Zap,          label: 'Flashcards'     },
      { to: '/attendance', icon: CalendarCheck, label: 'Attendance'    },
      { to: '/cgpa',       icon: GraduationCap, label: 'CGPA'          },
      { to: '/exams',      icon: BookMarked,   label: 'Exams'          },
      { to: '/timetable',  icon: Clock,        label: 'Timetable'      },
      { to: '/syllabus',   icon: FileText,     label: 'Syllabus'       },
    ],
  },
  {
    id: 'career', label: 'Career', zicon: Briefcase,
    gradient: '#1257a0', lightBg: '#FAF6EE',
    items: [
      { to: '/year-target',   icon: Target,    label: '4-Year Target'  },
      { to: '/scholarships',  icon: Award,     label: 'Scholarships'   },
      { to: '/opportunities', icon: Target,    label: 'Opportunities'  },
      { to: '/resume',        icon: FilePen,   label: 'Resume'         },
      { to: '/linkedin',      icon: Linkedin,  label: 'LinkedIn'       },
      { to: '/knowledge',     icon: Brain,     label: 'Knowledge Loop' },
      { to: '/projects',      icon: Code,      label: 'Projects'       },
    ],
  },
  {
    id: 'connect', label: 'Connect', zicon: Users,
    gradient: '#188038', lightBg: '#F0F5EF',
    items: [
      { to: '/network',      icon: Globe,     label: 'Network & Groups' },
      { to: '/social',       icon: Users,     label: 'Student Social'   },
      { to: '/hackathons',   icon: Code2,     label: 'Hackathons'       },
    ],
  },
  {
    id: 'campus', label: 'Campus', zicon: Home,
    gradient: '#b06000', lightBg: '#FAF7F1',
    items: [
      { to: '/sports',      icon: Trophy,      label: 'Sports'      },
      { to: '/initiatives', icon: Sparkles,    label: 'Initiatives' },
      { to: '/showcase',    icon: Trophy,      label: 'Showcase'    },
      { to: '/food',        icon: Utensils,    label: 'Food Share'  },
      { to: '/cab',         icon: Car,         label: 'Cab Share'   },
      { to: '/pg-finder',   icon: Home,        label: 'PG Finder'   },
      { to: '/marketplace', icon: ShoppingBag, label: 'Marketplace' },
    ],
  },
  {
    id: 'finance', label: 'Finance', zicon: Receipt,
    gradient: '#188038', lightBg: '#EEF4EC',
    items: [
      { to: '/expenses',   icon: Receipt,     label: 'Expenses'         },
      { to: '/documents',  icon: Shield,      label: 'Document Vault'   },
      { to: '/grievances', icon: AlertCircle, label: 'Grievances'       },
    ],
  },
];

const BOTTOM_TABS = [
  { zoneId: 'home',    to: '/',           icon: LayoutDashboard, label: 'Home'   },
  { zoneId: 'study',   to: '/notes',      icon: BookOpen,        label: 'Study'  },
  { zoneId: 'career',  to: '/opportunities', icon: Briefcase,    label: 'Career' },
  { zoneId: 'connect', to: '/social',     icon: Users,           label: 'Social' },
  { zoneId: 'campus',  to: '/campus-news',icon: Megaphone,       label: 'Campus' },
];

const ZONE_GRADIENTS = {
  home:    '#1a73e8',
  study:   '#1a73e8',
  career:  '#1257a0',
  connect: '#188038',
  campus:  '#b06000',
  finance: '#188038',
};

/* ── Nav item ── */
function SideNavItem({ item, gradient, collapsed }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-150 text-sm font-medium
         ${isActive
           ? 'text-gray-900 dark:text-white bg-gray-100 dark:bg-white/[0.08]'
           : 'text-gray-500 dark:text-white/50 hover:text-gray-800 dark:hover:text-white/80 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
         }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
            style={isActive ? { background: gradient } : {}}
          >
            <Icon
              size={16}
              className={isActive
                ? 'text-white'
                : 'text-gray-400 dark:text-white/40 group-hover:text-gray-600 dark:group-hover:text-white/70'}
            />
          </span>
          {!collapsed && <span className="truncate">{item.label}</span>}
          {isActive && !collapsed && (
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-500" />
          )}
        </>
      )}
    </NavLink>
  );
}

/* ── Zone section ── */
function ZoneSection({ zone, collapsed, openZones, toggleZone }) {
  const isOpen = openZones.has(zone.id);
  const location = useLocation();
  const isZoneActive = zone.items.some(
    i => location.pathname === i.to || (i.to !== '/' && location.pathname.startsWith(i.to))
  );

  return (
    <div className="mb-1">
      <button
        onClick={() => toggleZone(zone.id)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-150 group
          ${isZoneActive
            ? 'text-gray-900 dark:text-white'
            : 'text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
          }`}
        style={isZoneActive && zone.lightBg ? { background: zone.lightBg } : {}}
      >
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
          style={{ background: zone.gradient }}
        >
          {zone.zicon ? React.createElement(zone.zicon, { size: 15, className: 'text-white' }) : null}
        </span>
        {!collapsed && (
          <>
            <span className="text-xs font-bold uppercase tracking-wider flex-1 text-left">{zone.label}</span>
            <ChevronRight
              size={14}
              className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''} opacity-40`}
            />
          </>
        )}
      </button>

      {isOpen && !collapsed && (
        <div className="ml-2 pl-3 border-l border-gray-200 dark:border-white/[0.06] mt-1 space-y-0.5">
          {zone.items.map(item => (
            <SideNavItem key={item.to} item={item} gradient={zone.gradient} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Layout ── */
export default function Layout() {
  const { user, logout } = useAuth();
  const { flags } = useTheme();
  const { isOff } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide nav items whose domain is disabled server-side; drop zones left empty
  const navZones = NAV_ZONES
    .map(zone => ({ ...zone, items: zone.items.filter(item => !isOff(ROUTE_DOMAINS[item.to])) }))
    .filter(zone => zone.items.length > 0);
  const bottomTabs = BOTTOM_TABS.map(tab =>
    isOff(ROUTE_DOMAINS[tab.to]) ? { ...tab, to: '/' } : tab
  );
  const [collapsed, setCollapsed]   = useState(flags.compactSidebar);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openZones, setOpenZones]   = useState(
    flags.autoExpandZones
      ? new Set(NAV_ZONES.map(z => z.id))
      : new Set(['home'])
  );

  const toggleZone = id => setOpenZones(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'CC';

  const Sidebar = ({ mobile = false }) => (
    <aside
      className={`
        flex flex-col h-full overflow-hidden transition-all duration-300
        ${mobile ? 'w-72' : collapsed ? 'w-[68px]' : 'w-[260px]'}
        bg-white dark:bg-[#141420] border-r border-gray-200 dark:border-white/[0.06]
      `}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-gray-100 dark:border-white/[0.06] flex-shrink-0">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0 min-w-0" style={{ textDecoration: 'none' }}>
          <AuraLogo size={32} />
          {(!collapsed || mobile) && (
            <span className="font-black text-gray-900 dark:text-white text-lg tracking-tight">
              Au<span className="text-grad-home">ra</span>
            </span>
          )}
        </Link>
        {!mobile && (
          <button
            onClick={() => setCollapsed(c => !c)}
            className="ml-auto text-gray-400 dark:text-white/30 hover:text-gray-700 dark:hover:text-white/70 transition-colors"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
        {mobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Profile chip → Settings */}
      <NavLink
        to="/settings"
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]
           flex-shrink-0 transition-colors
           ${isActive
             ? 'bg-gray-100 dark:bg-white/[0.06]'
             : 'hover:bg-gray-50 dark:hover:bg-white/[0.04]'}
           ${collapsed && !mobile ? 'justify-center' : ''}`
        }
        title="Profile & Settings"
      >
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
          style={{
            background: '#1a73e8',
            boxShadow: '0 0 0 2px rgba(26,115,232,0.25)',
          }}
        >
          {initials}
        </div>
        {(!collapsed || mobile) && (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user?.name || 'Student'}
              </p>
              <p className="text-xs text-gray-400 dark:text-white/40 truncate">
                {user?.college || user?.email || ''}
              </p>
            </div>
            {user?.coinBalance != null && (
              <span className="flex items-center gap-0.5 text-xs font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full flex-shrink-0">
                🪙 {user.coinBalance}
              </span>
            )}
            <Settings size={14} className="text-gray-300 dark:text-white/25 flex-shrink-0" />
          </>
        )}
      </NavLink>

      {/* Nav zones */}
      <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navZones.map(zone => (
          <ZoneSection
            key={zone.id}
            zone={zone}
            collapsed={collapsed && !mobile}
            openZones={openZones}
            toggleZone={toggleZone}
          />
        ))}
      </div>

      {/* Bottom actions */}
      <div className="flex-shrink-0 px-2 py-3 border-t border-gray-100 dark:border-white/[0.06] space-y-0.5">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-sm font-medium
             ${isActive
               ? 'bg-gray-100 dark:bg-white/[0.08] text-gray-900 dark:text-white'
               : 'text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 hover:bg-gray-50 dark:hover:bg-white/[0.05]'}
             ${collapsed && !mobile ? 'justify-center' : ''}`
          }
        >
          <Settings size={16} />
          {(!collapsed || mobile) && <span>Settings</span>}
        </NavLink>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl
            text-gray-400 dark:text-white/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10
            transition-all text-sm font-medium
            ${collapsed && !mobile ? 'justify-center' : ''}`}
        >
          <LogOut size={16} />
          {(!collapsed || mobile) && <span>Sign out</span>}
        </button>

        {/* Contact line — hidden when sidebar is collapsed */}
        {(!collapsed || mobile) && (
          <p className="text-[10px] text-center text-gray-300 dark:text-white/15 pt-2 pb-1 leading-relaxed">
            <a
              href="mailto:contact@quantaiseed.com"
              className="hover:text-brand-500 dark:hover:text-brand-400 transition-colors"
            >
              contact@quantaiseed.com
            </a>
            <br />
            <span className="text-gray-200 dark:text-white/10">© Quantaiseed 2024</span>
          </p>
        )}
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex h-full">
        <Sidebar />
      </div>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 dark:bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 md:hidden transform transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <Sidebar mobile />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-gray-50 dark:bg-surface-900">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 h-14 border-b border-gray-100 dark:border-white/[0.06] flex-shrink-0 bg-white/90 dark:bg-surface-900/80 backdrop-blur-md">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-500 dark:text-white/60 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            <Menu size={20} />
          </button>
          <span className="font-black text-gray-900 dark:text-white text-base tracking-tight">
            Grade<span className="text-grad-home">2U</span>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 dark:text-white/50 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.07] transition-all">
              <Search size={16} />
            </button>
            <button className="relative w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 dark:text-white/50 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.07] transition-all">
              <Bell size={16} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-pink-500 rounded-full" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-30 bottom-nav-safe
            bg-white/95 dark:bg-[#0F0F18]/95
            border-t border-gray-100 dark:border-white/[0.06]
            backdrop-blur-xl"
        >
          <div className="flex items-center justify-around px-2 py-2">
            {bottomTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = tab.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(tab.to);
              const gradient = ZONE_GRADIENTS[tab.zoneId];
              return (
                <NavLink
                  key={tab.zoneId}
                  to={tab.to}
                  end={tab.to === '/'}
                  className="flex flex-col items-center gap-1 px-3 py-1 min-w-0"
                >
                  <span
                    className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-200"
                    style={isActive ? { background: gradient } : {}}
                  >
                    <Icon
                      size={18}
                      className={isActive
                        ? 'text-white'
                        : 'text-gray-400 dark:text-white/40'}
                    />
                  </span>
                  <span
                    className={`text-[10px] font-semibold transition-colors
                      ${isActive
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-white/30'}`}
                  >
                    {tab.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
